'use client'

import { useEffect, useId, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import Link from "next/link"
import Image from "next/image"
import { useConsent } from "../../../providers/ConsentProvider"
import { createObjectPagePathname, getAsServerMediaUrl, getMediaUrl } from "@/services/pathname"

type SpotlightProps = {
    // Website displaying the spotlights
    aroevenSlug: string,
    items: SpotlightItem[]
}

type Target = {
    href: string,
    // Opened in a new tab
    external: boolean
}

type State = 'hidden' | 'collapsed' | 'open'

const DEFAULT_POST_CTA_LABEL = 'Lire l\'actualité'
const DEFAULT_CUSTOM_CTA_LABEL = 'En savoir plus'
// The corner card opens after this delay, or once this part of the page is scrolled
const CORNER_OPEN_DELAY = 4000
const CORNER_OPEN_SCROLL_RATIO = 0.3
const MODAL_OPEN_DELAY = 1500

/**
 * SVG and not the icon font, whose cross glyph is not centered in its box
 */
function CloseIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    )
}

/**
 * Only its id and slug are used by createObjectPagePathname
 */
function getPostPathname(item: SpotlightItem) {
    return item.post ? createObjectPagePathname('Post', item.post as Post) : null
}

/**
 * Where the spotlight leads to : its post, or its link (a url, a media, or a page of the website which created it)
 */
function getTarget(item: SpotlightItem, aroevenSlug: string): Target | null {
    if (item.post) {
        return { href: getPostPathname(item), external: false }
    }
    if (item.link?.['@type'] === 'LinkUrl') {
        return { href: item.link.url, external: true }
    }
    if (item.link?.['@type'] === 'LinkMedia') {
        return { href: getMediaUrl(item.link.media.url), external: true }
    }
    if (item.link?.['@type'] === 'LinkObject' && item.link.pathname) {
        // Forced by the federation : a page of the federation website
        return { href: (item.aroeven !== aroevenSlug ? item.aroevenUrl : '') + item.link.pathname, external: false }
    }
    return null
}

function isRunning(item: SpotlightItem, now: number) {
    return (!item.startAt || new Date(item.startAt).getTime() <= now)
        && (!item.endAt || new Date(item.endAt).getTime() > now)
}

/**
 * dismissed : closed, reachable from the pill, for the visit only (sessionStorage) : displayed again on the next visit
 * read : its post or link opened, for good (localStorage) : neither the spotlight nor the pill are displayed anymore
 */
type Seen = 'dismissed' | 'read'

/**
 * Per version : a visitor who closed or read a spotlight sees it again once modified
 */
function getStorageKey(item: SpotlightItem) {
    return 'spotlight-' + item.id + '-' + item.version
}

function getStorage(seen: Seen): Storage {
    return seen === 'read' ? window.localStorage : window.sessionStorage
}

function getSeen(item: SpotlightItem): Seen | null {
    for (const seen of ['read', 'dismissed'] as Seen[]) {
        try {
            if (getStorage(seen).getItem(getStorageKey(item)) === seen) {
                return seen
            }
        }
        catch (error) { }
    }
    return null
}

function setSeen(item: SpotlightItem, seen: Seen) {
    try {
        getStorage(seen).setItem(getStorageKey(item), seen)
    }
    catch (error) { }
}

/**
 * No effect without consent, GTM (and its dataLayer) being loaded only once accepted
 */
function track(event: string, item: SpotlightItem) {
    window.dataLayer?.push({
        event,
        spotlight_id: item.id,
        spotlight_title: item.title,
        spotlight_display_mode: item.displayMode
    })
}

/**
 * "À la une" : a post highlighted as a corner card, a top bar or a modal
 * Resolved in the browser, the dates against the visitor clock : the cached pages stay valid
 */
export default function Spotlight({
    aroevenSlug,
    items
}: SpotlightProps) {

    const pathname = usePathname()
    const { showBanner } = useConsent()

    const [item, setItem] = useState<SpotlightItem | null>(null)
    const [state, setState] = useState<State>('hidden')
    // Reopened from the pill : a bar becomes the corner card, displayed again it would push the page down
    const [reopened, setReopened] = useState<boolean>(false)
    const mode = reopened && item?.displayMode === 'bar' ? 'corner' : item?.displayMode

    const titleId = useId()
    const dialogId = useId()
    const refBar = useRef<HTMLDivElement>(null)
    const refClose = useRef<HTMLButtonElement>(null)
    const refPill = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const running = items.find(item => isRunning(item, Date.now()) && getTarget(item, aroevenSlug) !== null) ?? null
        if (
            running === null
            || (running.pages === 'home' && pathname !== '/')
            || (running.post !== null && pathname === getPostPathname(running))
        ) {
            setItem(null)
            setState('hidden')
            setReopened(false)
            return
        }
        setItem(running)
    }, [items, pathname, aroevenSlug])

    // Displayed once the consent banner closed, both would overlap on mobile
    useEffect(() => {
        if (item === null || showBanner) {
            return
        }

        const seen = getSeen(item)
        if (seen !== null) {
            setState(seen === 'dismissed' ? 'collapsed' : 'hidden')
            return
        }

        if (item.displayMode === 'bar') {
            open()
            return
        }

        let opened = false
        const show = () => {
            if (opened) return
            opened = true
            cleanup()
            open()
        }
        const onScroll = () => {
            const scrollingElement = document.scrollingElement
            const scrollable = scrollingElement.scrollHeight - window.innerHeight
            if (scrollable > 0 && scrollingElement.scrollTop / scrollable >= CORNER_OPEN_SCROLL_RATIO) {
                show()
            }
        }
        const timeout = window.setTimeout(show, item.displayMode === 'modal' ? MODAL_OPEN_DELAY : CORNER_OPEN_DELAY)
        if (item.displayMode === 'corner') {
            window.addEventListener('scroll', onScroll, { passive: true })
        }
        function cleanup() {
            window.clearTimeout(timeout)
            window.removeEventListener('scroll', onScroll)
        }
        return cleanup
    }, [item, showBanner])

    useEffect(() => {
        if (item === null || state !== 'open') {
            return
        }
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                close()
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [item, state])

    // The modal takes the focus and locks the page scroll, the corner card and the bar don't
    useEffect(() => {
        if (mode !== 'modal' || state !== 'open') {
            return
        }
        const previousFocus = document.activeElement as HTMLElement | null
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        refClose.current?.focus()
        return () => {
            document.body.style.overflow = previousOverflow
            previousFocus?.focus?.()
        }
    }, [mode, state])

    // The bar is above the fixed header, which goes down by its height
    useEffect(() => {
        if (mode !== 'bar' || state !== 'open' || !refBar.current) {
            return
        }
        const body = document.body
        const observer = new ResizeObserver(() => {
            body.style.setProperty('--spotlight-bar-height', refBar.current.offsetHeight + 'px')
        })
        observer.observe(refBar.current)
        body.classList.add('has-spotlight-bar')
        return () => {
            observer.disconnect()
            body.classList.remove('has-spotlight-bar')
            body.style.removeProperty('--spotlight-bar-height')
        }
    }, [mode, state])

    function open() {
        setState('open')
        track('spotlight_view', item)
    }

    // Collapsed in the pill whatever its mode
    function close() {
        setSeen(item, 'dismissed')
        setState('collapsed')
        track('spotlight_close', item)
        window.requestAnimationFrame(() => refPill.current?.focus())
    }

    function reopen() {
        setReopened(true)
        setState('open')
        track('spotlight_reopen', item)
    }

    function onCtaClick() {
        setSeen(item, 'read')
        setState('hidden')
        track('spotlight_click', item)
    }

    if (item === null || state === 'hidden') {
        return null
    }

    const target = getTarget(item, aroevenSlug)
    const ctaLabel = item.ctaLabel || (item.post ? DEFAULT_POST_CTA_LABEL : DEFAULT_CUSTOM_CTA_LABEL)

    if (state === 'open' && mode === 'bar') {
        return (
            <div ref={refBar} className="spotlight-bar" role="region" aria-label="À la une">
                <div className="spotlight-bar__content">
                    <i className="fi fi-rr-megaphone spotlight-bar__icon" aria-hidden="true" />
                    <span className="spotlight-bar__label">À la une</span>
                    <TargetLink target={target} className="spotlight-bar__title" onClick={onCtaClick}>
                        {item.title}
                        <span className="spotlight-bar__cta">{ctaLabel}</span>
                    </TargetLink>
                </div>
                <button type="button" className="spotlight-bar__close" aria-label="Fermer" onClick={close}>
                    <CloseIcon />
                </button>
            </div>
        )
    }

    if (state === 'collapsed') {
        return (
            <button ref={refPill} type="button" className="spotlight-pill" aria-expanded="false" onClick={reopen}>
                <i className="fi fi-rr-megaphone" aria-hidden="true" />
                <span>À la une</span>
            </button>
        )
    }

    const card = (
        <div
            id={dialogId}
            className={'spotlight spotlight--' + mode}
            role="dialog"
            aria-modal={mode === 'modal'}
            aria-labelledby={titleId}
        >
            <button ref={refClose} type="button" className="spotlight__close" aria-label="Fermer" onClick={close}>
                <CloseIcon />
            </button>
            {item.image &&
                <div className="spotlight__image">
                    <Image
                        src={getAsServerMediaUrl(item.image.url)}
                        fill
                        sizes={mode === 'modal' ? '(max-width: 768px) 100vw, 320px' : '320px'}
                        quality={60}
                        alt=""
                    />
                </div>
            }
            <div className="spotlight__body">
                <span className="spotlight__badge">À la une</span>
                {/* Not <p> : the theme styles every paragraph (color, justify, font) */}
                <div className="spotlight__title" id={titleId}>{item.title}</div>
                {item.excerpt && <div className="spotlight__excerpt">{item.excerpt}</div>}
                <TargetLink target={target} className="spotlight__cta" onClick={onCtaClick}>{ctaLabel}</TargetLink>
            </div>
        </div>
    )

    if (mode === 'modal') {
        return (
            <div className="spotlight-backdrop" onClick={(e) => e.target === e.currentTarget && close()}>
                {card}
            </div>
        )
    }

    return card
}

type TargetLinkProps = {
    target: Target,
    className: string,
    onClick: () => void,
    children: React.ReactNode
}

function TargetLink({
    target,
    className,
    onClick,
    children
}: TargetLinkProps) {
    if (target.external) {
        return <a href={target.href} className={className} onClick={onClick} target="_blank" rel="noopener noreferrer">{children}</a>
    }
    return <Link href={target.href} className={className} onClick={onClick}>{children}</Link>
}
